import { WebRTCConnectionState, WebRTCMessage, OnlineMeetingSession } from '@/types';

// Public STUN servers for NAT traversal across different cities and ISPs
export const PUBLIC_STUN_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
  { urls: 'stun:global.stun.twilio.com:3478' },
  { urls: 'stun:stun.services.mozilla.com' }
];

export interface WebRTCEvents {
  onStatusChange?: (status: WebRTCConnectionState) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onIncomingCallRequest?: (callerId: string, accept: () => void, decline: () => void) => void;
  onRecordingConsentRequest?: (hostName: string) => void;
  onRecordingConsentDelivered?: () => void;
  onRecordingConsentResponse?: (granted: boolean) => void;
  onRecordingStarted?: () => void;
  onRecordingStopped?: () => void;
  onRemoteMediaStateChange?: (state: { audioMuted: boolean; videoOff: boolean }) => void;
  onLatencyUpdate?: (rttMs: number) => void;
  onError?: (errMessage: string) => void;
}

/**
 * Munges WebRTC Session Description Protocol (SDP) to enforce:
 * 1. b=AS and b=TIAS bandwidth allocations on video (4000 kbps / 4 Mbps)
 * 2. Chromium Google Congestion Control (GCC) bitrate floor & start parameters:
 *    - x-google-min-bitrate=2000 (kbps): NEVER drop below 2 Mbps under any congestion!
 *    - x-google-start-bitrate=3500 (kbps): Starts crystal-clear immediately without blur
 *    - x-google-max-bitrate=6000 (kbps): Allows pristine 1080p
 * This completely prevents WebRTC GCC from downscaling resolution to 240p/144p after 1 minute!
 */
export function enhanceSDPWithVideoBitrate(sdp: string, bitrateKbps: number = 4000): string {
  if (!sdp) return sdp;
  const lines = sdp.split(/\r\n|\n/);
  let inVideoSection = false;
  const newLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('m=video')) {
      inVideoSection = true;
      newLines.push(line);
      newLines.push(`b=AS:${bitrateKbps}`);
      newLines.push(`b=TIAS:${bitrateKbps * 1000}`);
      continue;
    } else if (line.startsWith('m=')) {
      inVideoSection = false;
    }

    if (inVideoSection) {
      if (line.startsWith('b=AS:') || line.startsWith('b=TIAS:')) {
        continue;
      }

      if (line.startsWith('a=fmtp:')) {
        let fmtpLine = line;
        if (!fmtpLine.includes('x-google-min-bitrate')) {
          fmtpLine += ';x-google-min-bitrate=2000;x-google-max-bitrate=6000;x-google-start-bitrate=3500';
        }
        newLines.push(fmtpLine);
        continue;
      }
    }

    newLines.push(line);
  }

  return newLines.join('\r\n');
}

class WebRTCMeetingService {
  private peer: any = null; // PeerJS instance (dynamic load/fallback)
  private peerCall: any = null;
  private peerDataConn: any = null;
  private nativePc: RTCPeerConnection | null = null;
  private nativeDataChannel: RTCDataChannel | null = null;

  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;

  private events: WebRTCEvents = {};
  private status: WebRTCConnectionState = 'idle';
  private connectionMode: 'anydesk-broker' | 'manual-p2p' = 'anydesk-broker';

  private callStartTime: number | null = null;
  private callDurationInterval: any = null;
  private callDurationSec: number = 0;
  private pingInterval: any = null;
  private keyframeInterval: any = null;

  private meetingId: string = '';
  private isHost: boolean = false;
  private recordingConsentGranted: boolean = false;
  private targetPeerId: string = '';
  private pendingMessages: WebRTCMessage[] = [];
  private consentRetryInterval: any = null;
  private consentRetryCount: number = 0;

  // Set event callbacks
  public setEvents(events: WebRTCEvents) {
    this.events = { ...this.events, ...events };
  }

  public getStatus(): WebRTCConnectionState {
    return this.status;
  }

  public getCallDuration(): number {
    return this.callDurationSec;
  }

  public getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }

  public isConsentGranted(): boolean {
    return this.recordingConsentGranted;
  }

  public getPeerConnection(): RTCPeerConnection | null {
    if (this.peerCall?.peerConnection) {
      return this.peerCall.peerConnection;
    }
    if (this.nativePc) {
      return this.nativePc;
    }
    return null;
  }

  /**
   * Applies broadcast-quality WebRTC sender parameters:
   * 1. contentHint = 'detail' (instructs Chromium to prioritize sharpness)
   * 2. degradationPreference = 'maintain-resolution' (NEVER downscale resolution to blur/pixelate)
   * 3. maxBitrate = 4,000,000 bps (4 Mbps)
   */
  public async optimizePeerConnection(pc: RTCPeerConnection | null) {
    if (!pc) return;
    try {
      const senders = pc.getSenders();
      for (const sender of senders) {
        if (sender.track && sender.track.kind === 'video') {
          try {
            sender.track.contentHint = 'detail';
          } catch {}

          try {
            const params = sender.getParameters();
            if (!params.encodings || params.encodings.length === 0) {
              params.encodings = [{}];
            }
            params.encodings[0].maxBitrate = 4000000;
            params.encodings[0].networkPriority = 'high';
            params.encodings[0].priority = 'high';
            // CRITICAL FIX: Strictly forbid WebRTC from dropping video resolution!
            params.degradationPreference = 'maintain-resolution';

            await sender.setParameters(params);
            console.log('[WebRTC Quality] Sender parameters locked: maintain-resolution @ 4Mbps');
          } catch (paramErr) {
            // Some browsers do not allow setParameters before remote description
          }
        }
      }
    } catch (err) {
      console.warn('[WebRTC Quality] optimizePeerConnection warning:', err);
    }
  }

  /**
   * Forces the video encoder to immediately generate a clean IDR keyframe.
   * Clears any macroblock smear, blur, or decoder corruption instantly.
   */
  public forceKeyFrame() {
    const pc = this.getPeerConnection();
    if (pc) {
      pc.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'video') {
          if (typeof (sender as any).generateKeyFrame === 'function') {
            (sender as any).generateKeyFrame().catch(() => {});
          }
        }
      });
    }
  }

  /**
   * Sends a request over the DataChannel asking the remote peer to immediately send an IDR keyframe.
   */
  public requestRemoteKeyFrame() {
    this.sendMessage({ type: 'REQUEST_KEYFRAME' });
  }

  private startKeyframeKeeper() {
    this.stopKeyframeKeeper();
    // Periodically re-enforce maintain-resolution and emit IDR keyframes every 2.5 seconds
    this.keyframeInterval = setInterval(() => {
      if (this.status === 'connected') {
        const pc = this.getPeerConnection();
        if (pc) {
          this.optimizePeerConnection(pc);
          this.forceKeyFrame();
        }
      }
    }, 2500);
  }

  private stopKeyframeKeeper() {
    if (this.keyframeInterval) {
      clearInterval(this.keyframeInterval);
      this.keyframeInterval = null;
    }
  }

  private setStatus(newStatus: WebRTCConnectionState) {
    this.status = newStatus;
    if (newStatus === 'connected') {
      this.startDurationTimer();
      this.startPingMonitor();
      this.startKeyframeKeeper();
      this.optimizePeerConnection(this.getPeerConnection());
    } else if (newStatus === 'disconnected' || newStatus === 'failed' || newStatus === 'idle') {
      this.stopDurationTimer();
      this.stopPingMonitor();
      this.stopKeyframeKeeper();
    }
    if (this.events.onStatusChange) {
      this.events.onStatusChange(newStatus);
    }
  }

  // --- ANYDESK-STYLE BROKER CONNECTION (6-DIGIT ID) ---

  /**
   * Initializes host AnyDesk-style room with a clean 6-digit code.
   * Uses free public WebRTC broker (0.peerjs.com) with STUN.
   */
  public async initHostBroker(meetingCode: string, localMediaStream: MediaStream): Promise<string> {
    this.disconnect();
    this.isHost = true;
    this.meetingId = meetingCode;
    this.localStream = localMediaStream;
    this.connectionMode = 'anydesk-broker';
    this.setStatus('initializing');

    try {
      const PeerModule = await import('peerjs');
      const PeerClass = (PeerModule.default || PeerModule.Peer || PeerModule) as any;

      // Prefix meeting code to guarantee unique namespace on public broker
      const fullPeerId = `meetsplit-${meetingCode.replace(/\s+/g, '').toLowerCase()}`;

      this.peer = new PeerClass(fullPeerId, {
        config: {
          iceServers: PUBLIC_STUN_SERVERS
        },
        debug: 1
      });

      return new Promise((resolve, reject) => {
        this.peer.on('open', (id: string) => {
          console.log('[WebRTC Host] Registered on broker with ID:', id);
          this.setStatus('waiting');
          resolve(meetingCode);
        });

        this.peer.on('connection', (conn: any) => {
          console.log('[WebRTC Host] Inbound data connection from:', conn.peer);
          this.targetPeerId = conn.peer;
          this.setupDataConnectionHandlers(conn);
        });

        this.peer.on('call', (call: any) => {
          console.log('[WebRTC Host] Inbound call received from:', call.peer);
          this.targetPeerId = call.peer;
          // Preemptively ensure host has open bidirectional data connection to guest
          this.ensureDataConnectionToPeer(call.peer);

          // Trigger AnyDesk-style Accept / Decline prompt
          if (this.events.onIncomingCallRequest) {
            this.events.onIncomingCallRequest(
              call.peer,
              () => {
                // User accepted incoming connection
                this.setStatus('connecting');
                this.peerCall = call;
                this.ensureDataConnectionToPeer(call.peer);
                call.answer(this.localStream, {
                  sdpTransform: (sdp: string) => enhanceSDPWithVideoBitrate(sdp, 4000)
                });
                this.setupCallHandlers(call);
              },
              () => {
                // User declined incoming connection
                call.close();
                this.setStatus('waiting');
              }
            );
          } else {
            // Default: auto answer if no prompt hook registered
            this.setStatus('connecting');
            this.peerCall = call;
            this.ensureDataConnectionToPeer(call.peer);
            call.answer(this.localStream, {
              sdpTransform: (sdp: string) => enhanceSDPWithVideoBitrate(sdp, 4000)
            });
            this.setupCallHandlers(call);
          }
        });

        this.peer.on('error', (err: any) => {
          console.warn('[WebRTC Host Error]', err);
          if (err.type === 'unavailable-id') {
            this.events.onError?.('Meeting ID is already active. Please pick another code.');
          } else {
            this.events.onError?.(`Network error: ${err.message || 'Unable to connect to STUN broker'}`);
          }
          this.setStatus('failed');
          reject(err);
        });

        this.peer.on('disconnected', () => {
          console.log('[WebRTC Host] Disconnected from broker, attempting reconnect...');
          this.peer?.reconnect();
        });
      });
    } catch (err: any) {
      console.error('[WebRTC Host Init Error]', err);
      this.setStatus('failed');
      this.events.onError?.(err.message || 'Failed to initialize P2P engine');
      throw err;
    }
  }

  /**
   * Proactively opens or verifies a JSON DataConnection to the remote peer.
   */
  public ensureDataConnectionToPeer(remotePeerId: string) {
    if (!this.peer || this.peer.destroyed || !remotePeerId) return;
    if (this.peerDataConn && this.peerDataConn.open) {
      console.log('[WebRTC DataChannel] Connection already active & open to:', this.peerDataConn.peer);
      return;
    }
    console.log('[WebRTC DataChannel] Proactively establishing DataConnection to:', remotePeerId);
    try {
      const conn = this.peer.connect(remotePeerId, {
        reliable: true,
        serialization: 'json'
      });
      this.setupDataConnectionHandlers(conn);
    } catch (err) {
      console.warn('[WebRTC DataChannel] Failed to initiate data connection to:', remotePeerId, err);
    }
  }

  /**
   * Joins a host's room using their 6-digit meeting code.
   */
  public async joinGuestBroker(hostMeetingCode: string, localMediaStream: MediaStream): Promise<void> {
    this.disconnect();
    this.isHost = false;
    this.meetingId = hostMeetingCode;
    this.localStream = localMediaStream;
    this.connectionMode = 'anydesk-broker';
    this.setStatus('connecting');

    try {
      const PeerModule = await import('peerjs');
      const PeerClass = (PeerModule.default || PeerModule.Peer || PeerModule) as any;

      // Guest gets a random temporary peer ID
      const guestPeerId = `meetsplit-guest-${Math.random().toString(36).substring(2, 9)}`;

      this.peer = new PeerClass(guestPeerId, {
        config: {
          iceServers: PUBLIC_STUN_SERVERS
        },
        debug: 1
      });

      const targetHostPeerId = `meetsplit-${hostMeetingCode.replace(/\s+/g, '').toLowerCase()}`;

      this.peer.on('open', () => {
        console.log('[WebRTC Guest] Registered with ID:', guestPeerId, 'Calling host:', targetHostPeerId);
        this.targetPeerId = targetHostPeerId;

        // 1. Establish DataConnection with explicit JSON serialization for signaling & consent
        const conn = this.peer.connect(targetHostPeerId, {
          reliable: true,
          serialization: 'json'
        });
        this.setupDataConnectionHandlers(conn);

        // 2. Call host with local camera/mic stream using enhanced 4Mbps SDP
        const call = this.peer.call(targetHostPeerId, this.localStream, {
          sdpTransform: (sdp: string) => enhanceSDPWithVideoBitrate(sdp, 4000)
        });
        this.peerCall = call;
        this.setupCallHandlers(call);
      });

      this.peer.on('connection', (conn: any) => {
        console.log('[WebRTC Guest] Inbound data connection from host:', conn.peer);
        this.setupDataConnectionHandlers(conn);
      });

      this.peer.on('error', (err: any) => {
        console.warn('[WebRTC Guest Error]', err);
        if (err.type === 'peer-unavailable') {
          this.events.onError?.(`Host code "${hostMeetingCode}" was not found or is offline.`);
        } else {
          this.events.onError?.(`Connection error: ${err.message || 'Unable to reach peer'}`);
        }
        this.setStatus('failed');
      });
    } catch (err: any) {
      console.error('[WebRTC Guest Join Error]', err);
      this.setStatus('failed');
      this.events.onError?.(err.message || 'Failed to connect to host');
      throw err;
    }
  }

  public updateLocalStream(newStream: MediaStream) {
    this.localStream = newStream;
    newStream.getVideoTracks().forEach((track) => {
      try {
        track.contentHint = 'detail';
      } catch {}
    });

    try {
      const pc = this.getPeerConnection();
      if (pc) {
        const senders: RTCRtpSender[] = pc.getSenders();
        newStream.getTracks().forEach((track) => {
          const sender = senders.find((s) => s.track && s.track.kind === track.kind);
          if (sender) {
            sender.replaceTrack(track).then(() => {
              this.optimizePeerConnection(pc);
              this.forceKeyFrame();
            }).catch((e) => console.warn('[WebRTC] replaceTrack warning:', e));
          } else {
            pc.addTrack(track, newStream);
            this.optimizePeerConnection(pc);
            this.forceKeyFrame();
          }
        });
      }
    } catch (err) {
      console.warn('[WebRTC] updateLocalStream error:', err);
    }
  }

  private setupCallHandlers(call: any) {
    const attachStream = (remoteMediaStream: MediaStream) => {
      console.log('[WebRTC] Remote media stream tracks:', remoteMediaStream.getTracks().map(t => `${t.kind}:${t.readyState}`));
      remoteMediaStream.getVideoTracks().forEach(t => {
        try { t.contentHint = 'detail'; } catch {}
      });
      this.remoteStream = remoteMediaStream;
      this.setStatus('connected');
      if (this.events.onRemoteStream) {
        this.events.onRemoteStream(remoteMediaStream);
      }
    };

    call.on('stream', (remoteMediaStream: MediaStream) => {
      attachStream(remoteMediaStream);
    });

    // Also attach native peerConnection ontrack listener to capture asynchronously arriving tracks
    if (call.peerConnection) {
      const pc: RTCPeerConnection = call.peerConnection;
      this.optimizePeerConnection(pc);
      pc.addEventListener('connectionstatechange', () => {
        if (pc.connectionState === 'connected') {
          this.optimizePeerConnection(pc);
          this.forceKeyFrame();
        }
      });
      pc.ontrack = (event: RTCTrackEvent) => {
        console.log('[WebRTC peerConnection ontrack]', event.track.kind, event.streams);
        if (event.streams && event.streams[0]) {
          attachStream(event.streams[0]);
        } else {
          if (!this.remoteStream) {
            this.remoteStream = new MediaStream();
          }
          if (!this.remoteStream.getTracks().some((t) => t.id === event.track.id)) {
            this.remoteStream.addTrack(event.track);
          }
          attachStream(this.remoteStream);
        }
      };
    }

    call.on('close', () => {
      console.log('[WebRTC] Call closed by remote peer');
      this.setStatus('disconnected');
    });

    call.on('error', (err: any) => {
      console.warn('[WebRTC Call Error]', err);
      this.setStatus('failed');
    });
  }

  private setupDataConnectionHandlers(conn: any) {
    if (!conn) return;

    const onChannelReady = () => {
      console.log('[WebRTC DataChannel] Handshake OPEN with peer:', conn.peer);
      this.peerDataConn = conn;
      this.targetPeerId = conn.peer;
      this.flushPendingMessages();
    };

    if (conn.open) {
      onChannelReady();
    } else {
      conn.on('open', onChannelReady);
    }

    conn.on('data', (raw: any) => {
      this.handleIncomingMessage(raw);
    });

    conn.on('error', (err: any) => {
      console.warn('[WebRTC DataChannel Warning on', conn.peer, ']:', err);
    });

    conn.on('close', () => {
      console.log('[WebRTC DataChannel] Closed with peer:', conn.peer);
      if (this.peerDataConn === conn) {
        this.peerDataConn = null;
      }
    });
  }

  // --- MANUAL OFFER / ANSWER CODE & QR CODE (100% SERVERLESS) ---

  /**
   * Creates a manual WebRTC Offer with all ICE candidates bundled.
   * Returns a compressed base64 code string ready for copying or QR code rendering.
   */
  public async createManualOffer(localMediaStream: MediaStream): Promise<string> {
    this.disconnect();
    this.isHost = true;
    this.localStream = localMediaStream;
    this.connectionMode = 'manual-p2p';
    this.setStatus('initializing');

    const pc = new RTCPeerConnection({
      iceServers: PUBLIC_STUN_SERVERS
    });
    this.nativePc = pc;

    // Add local tracks to native peer connection
    localMediaStream.getTracks().forEach((track) => {
      pc.addTrack(track, localMediaStream);
    });

    // Create Data Channel on Host
    const dc = pc.createDataChannel('meetsplit-data', { ordered: true });
    this.nativeDataChannel = dc;
    this.setupNativeDataChannel(dc);

    // Setup remote track receiver
    pc.ontrack = (event) => {
      console.log('[WebRTC Native Host ontrack]', event.track.kind, event.streams);
      if (event.streams && event.streams[0]) {
        this.remoteStream = event.streams[0];
      } else {
        if (!this.remoteStream) {
          this.remoteStream = new MediaStream();
        }
        if (!this.remoteStream.getTracks().some((t) => t.id === event.track.id)) {
          this.remoteStream.addTrack(event.track);
        }
      }
      this.setStatus('connected');
      this.events.onRemoteStream?.(this.remoteStream);
    };

    pc.onconnectionstatechange = () => {
      console.log('[WebRTC Native] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        this.setStatus('connected');
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.setStatus(pc.connectionState === 'failed' ? 'failed' : 'disconnected');
      }
    };

    // Create offer with enhanced 4Mbps video bitrate SDP
    const offer = await pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    });
    const enhancedOfferSdp = enhanceSDPWithVideoBitrate(offer.sdp || '', 4000);
    await pc.setLocalDescription({ type: 'offer', sdp: enhancedOfferSdp });

    // Gather ICE candidates until complete (or 4s timeout)
    await this.waitForIceGathering(pc);

    this.setStatus('waiting');
    const payload = {
      type: 'OFFER',
      sdp: pc.localDescription?.sdp
    };

    return this.compressPayload(payload);
  }

  /**
   * Guest accepts manual offer code string, creates Answer with ICE candidates.
   * Returns compressed Answer code string.
   */
  public async acceptManualOffer(offerCodeString: string, localMediaStream: MediaStream): Promise<string> {
    this.disconnect();
    this.isHost = false;
    this.localStream = localMediaStream;
    this.connectionMode = 'manual-p2p';
    this.setStatus('connecting');

    const offerPayload = this.decompressPayload(offerCodeString);
    if (!offerPayload || offerPayload.type !== 'OFFER' || !offerPayload.sdp) {
      throw new Error('Invalid Offer Code format. Please check and try again.');
    }

    const pc = new RTCPeerConnection({
      iceServers: PUBLIC_STUN_SERVERS
    });
    this.nativePc = pc;

    localMediaStream.getTracks().forEach((track) => {
      try { track.contentHint = 'detail'; } catch {}
      pc.addTrack(track, localMediaStream);
    });

    pc.ondatachannel = (event) => {
      this.nativeDataChannel = event.channel;
      this.setupNativeDataChannel(event.channel);
    };

    pc.ontrack = (event) => {
      console.log('[WebRTC Native Guest ontrack]', event.track.kind, event.streams);
      if (event.streams && event.streams[0]) {
        event.streams[0].getVideoTracks().forEach(t => {
          try { t.contentHint = 'detail'; } catch {}
        });
        this.remoteStream = event.streams[0];
      } else {
        if (!this.remoteStream) {
          this.remoteStream = new MediaStream();
        }
        if (!this.remoteStream.getTracks().some((t) => t.id === event.track.id)) {
          this.remoteStream.addTrack(event.track);
        }
      }
      this.setStatus('connected');
      this.events.onRemoteStream?.(this.remoteStream);
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        this.optimizePeerConnection(pc);
        this.forceKeyFrame();
        this.setStatus('connected');
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.setStatus(pc.connectionState === 'failed' ? 'failed' : 'disconnected');
      }
    };

    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: offerPayload.sdp }));
    const answer = await pc.createAnswer();
    const enhancedAnswerSdp = enhanceSDPWithVideoBitrate(answer.sdp || '', 4000);
    await pc.setLocalDescription({ type: 'answer', sdp: enhancedAnswerSdp });

    await this.waitForIceGathering(pc);

    const answerPayload = {
      type: 'ANSWER',
      sdp: pc.localDescription?.sdp
    };

    return this.compressPayload(answerPayload);
  }

  /**
   * Host applies the Guest's Answer code to complete the P2P connection.
   */
  public async applyManualAnswer(answerCodeString: string): Promise<void> {
    if (!this.nativePc) {
      throw new Error('No active peer connection to apply answer to.');
    }

    const answerPayload = this.decompressPayload(answerCodeString);
    if (!answerPayload || answerPayload.type !== 'ANSWER' || !answerPayload.sdp) {
      throw new Error('Invalid Answer Code format. Please check and try again.');
    }

    this.setStatus('connecting');
    await this.nativePc.setRemoteDescription(
      new RTCSessionDescription({ type: 'answer', sdp: answerPayload.sdp })
    );
    console.log('[WebRTC Native] Remote answer applied successfully!');
  }

  private waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const checkState = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', checkState);
          resolve();
        }
      };

      pc.addEventListener('icegatheringstatechange', checkState);

      // 4-second safety timeout so user isn't kept waiting if a candidate delays
      setTimeout(() => {
        pc.removeEventListener('icegatheringstatechange', checkState);
        resolve();
      }, 4000);
    });
  }

  private setupNativeDataChannel(dc: RTCDataChannel) {
    dc.onopen = () => {
      console.log('[Native DataChannel] Open');
    };
    dc.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        this.handleIncomingMessage(msg);
      } catch (err) {
        console.warn('Failed to parse native data message', err);
      }
    };
  }

  // --- COMPACT CODE ENCODING & DECODING (FOR MANUAL / QR CODES) ---

  private compressPayload(obj: any): string {
    try {
      const jsonStr = JSON.stringify(obj);
      // Clean up SDP formatting for high compression ratio
      return btoa(encodeURIComponent(jsonStr));
    } catch {
      return btoa(JSON.stringify(obj));
    }
  }

  private decompressPayload(codeStr: string): any {
    try {
      const clean = codeStr.trim();
      const decoded = decodeURIComponent(atob(clean));
      return JSON.parse(decoded);
    } catch {
      try {
        return JSON.parse(atob(codeStr.trim()));
      } catch {
        return null;
      }
    }
  }

  // --- MESSAGING & RECORDING CONSENT PROTOCOL ---

  public sendMessage(msg: WebRTCMessage) {
    let sent = false;

    if (this.peerDataConn && this.peerDataConn.open) {
      try {
        this.peerDataConn.send(msg);
        sent = true;
      } catch (err) {
        console.warn('[WebRTC Signaling] Failed sending via peerDataConn:', err);
      }
    }

    if (!sent && this.nativeDataChannel && this.nativeDataChannel.readyState === 'open') {
      try {
        this.nativeDataChannel.send(JSON.stringify(msg));
        sent = true;
      } catch (err) {
        console.warn('[WebRTC Signaling] Failed sending via nativeDataChannel:', err);
      }
    }

    if (!sent) {
      console.log('[WebRTC Signaling] DataChannel not open yet. Queued message:', msg.type);
      this.pendingMessages.push(msg);
      if (this.targetPeerId && this.peer && !this.peer.destroyed) {
        this.ensureDataConnectionToPeer(this.targetPeerId);
      }
    }
  }

  private flushPendingMessages() {
    if (this.pendingMessages.length === 0) return;
    console.log(`[WebRTC Signaling] Flushing ${this.pendingMessages.length} pending queued messages...`);
    const queued = [...this.pendingMessages];
    this.pendingMessages = [];
    for (const msg of queued) {
      this.sendMessage(msg);
    }
  }

  private handleIncomingMessage(rawMsg: any) {
    let msg: any = rawMsg;
    if (typeof rawMsg === 'string') {
      try {
        msg = JSON.parse(rawMsg);
      } catch (e) {
        console.warn('[WebRTC] Failed to parse string message:', rawMsg);
        return;
      }
    } else if (rawMsg instanceof ArrayBuffer || (typeof Uint8Array !== 'undefined' && rawMsg instanceof Uint8Array)) {
      try {
        const text = new TextDecoder().decode(rawMsg);
        msg = JSON.parse(text);
      } catch (e) {
        console.warn('[WebRTC] Failed to decode binary message');
        return;
      }
    }

    if (!msg || !msg.type) return;
    console.log('[WebRTC Signaling Inbound]', msg.type, msg.payload);

    switch (msg.type) {
      case 'RECORDING_REQUEST':
        // Acknowledge receipt to host immediately so host knows it was delivered!
        this.sendMessage({ type: 'RECORDING_REQUEST_ACK', payload: { hostName: msg.payload?.hostName } });
        this.events.onRecordingConsentRequest?.(msg.payload?.hostName || 'Host');
        break;

      case 'RECORDING_REQUEST_ACK':
        console.log('[WebRTC Host] Guest ACK received! Consent prompt is active on Guest screen.');
        this.stopConsentRetry();
        this.events.onRecordingConsentDelivered?.();
        break;

      case 'RECORDING_CONSENT_GRANTED':
        this.stopConsentRetry();
        this.recordingConsentGranted = true;
        this.events.onRecordingConsentResponse?.(true);
        break;

      case 'RECORDING_CONSENT_DENIED':
        this.stopConsentRetry();
        this.recordingConsentGranted = false;
        this.events.onRecordingConsentResponse?.(false);
        break;

      case 'RECORDING_STARTED':
        this.forceKeyFrame();
        this.events.onRecordingStarted?.();
        break;

      case 'REQUEST_KEYFRAME':
        this.forceKeyFrame();
        break;

      case 'RECORDING_STOPPED':
        this.events.onRecordingStopped?.();
        break;

      case 'REMOTE_MEDIA_STATE':
        this.events.onRemoteMediaStateChange?.(msg.payload);
        break;

      case 'PING':
        this.sendMessage({ type: 'PONG', payload: { timestamp: msg.payload?.timestamp } });
        break;

      case 'PONG':
        if (msg.payload?.timestamp) {
          const rtt = Date.now() - msg.payload.timestamp;
          this.events.onLatencyUpdate?.(rtt);
        }
        break;
    }
  }

  /**
   * Host requests recording consent from remote guest.
   * Auto-resends every 1.5s until ACK or response is received.
   */
  public requestRecordingConsent(hostName: string) {
    this.stopConsentRetry();
    this.consentRetryCount = 0;

    // Send first attempt immediately
    this.sendMessage({
      type: 'RECORDING_REQUEST',
      payload: { hostName, timestamp: Date.now() }
    });

    // Auto-resend every 1.5 seconds up to 8 times until ACK or response is received
    this.consentRetryInterval = setInterval(() => {
      this.consentRetryCount++;
      if (this.recordingConsentGranted || this.consentRetryCount > 8) {
        this.stopConsentRetry();
        return;
      }
      console.log(`[WebRTC Host] Resending RECORDING_REQUEST (attempt ${this.consentRetryCount}/8)...`);
      this.sendMessage({
        type: 'RECORDING_REQUEST',
        payload: { hostName, timestamp: Date.now() }
      });
    }, 1500);
  }

  private stopConsentRetry() {
    if (this.consentRetryInterval) {
      clearInterval(this.consentRetryInterval);
      this.consentRetryInterval = null;
    }
  }

  /**
   * Guest responds with approval or rejection.
   */
  public respondToRecordingConsent(granted: boolean) {
    this.recordingConsentGranted = granted;
    this.sendMessage({
      type: granted ? 'RECORDING_CONSENT_GRANTED' : 'RECORDING_CONSENT_DENIED'
    });
  }

  public notifyRecordingStarted() {
    this.sendMessage({ type: 'RECORDING_STARTED' });
  }

  public notifyRecordingStopped() {
    this.sendMessage({ type: 'RECORDING_STOPPED' });
  }

  public broadcastMediaState(audioMuted: boolean, videoOff: boolean) {
    this.sendMessage({
      type: 'REMOTE_MEDIA_STATE',
      payload: { audioMuted, videoOff }
    });
  }

  // --- TIMERS & DIAGNOSTICS ---

  private startDurationTimer() {
    this.stopDurationTimer();
    this.callStartTime = Date.now();
    this.callDurationSec = 0;
    this.callDurationInterval = setInterval(() => {
      if (this.callStartTime) {
        this.callDurationSec = Math.floor((Date.now() - this.callStartTime) / 1000);
      }
    }, 1000);
  }

  private stopDurationTimer() {
    if (this.callDurationInterval) {
      clearInterval(this.callDurationInterval);
      this.callDurationInterval = null;
    }
  }

  private startPingMonitor() {
    this.stopPingMonitor();
    this.pingInterval = setInterval(() => {
      if (this.status === 'connected') {
        this.sendMessage({ type: 'PING', payload: { timestamp: Date.now() } });
      }
    }, 3000);
  }

  private stopPingMonitor() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // --- DISCONNECT & TEARDOWN ---

  public disconnect() {
    this.stopDurationTimer();
    this.stopPingMonitor();
    this.stopKeyframeKeeper();
    this.stopConsentRetry();

    this.pendingMessages = [];
    this.targetPeerId = '';

    // Close PeerJS connections
    if (this.peerCall) {
      try { this.peerCall.close(); } catch {}
      this.peerCall = null;
    }
    if (this.peerDataConn) {
      try { this.peerDataConn.close(); } catch {}
      this.peerDataConn = null;
    }
    if (this.peer) {
      try { this.peer.destroy(); } catch {}
      this.peer = null;
    }

    // Close Native RTCPeerConnection
    if (this.nativeDataChannel) {
      try { this.nativeDataChannel.close(); } catch {}
      this.nativeDataChannel = null;
    }
    if (this.nativePc) {
      try { this.nativePc.close(); } catch {}
      this.nativePc = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach(t => {
        try { t.stop(); } catch {}
      });
      this.localStream = null;
    }

    this.remoteStream = null;
    this.callDurationSec = 0;
    this.callStartTime = null;
    this.recordingConsentGranted = false;
    this.setStatus('idle');
  }
}


export const webrtcMeetingService = new WebRTCMeetingService();
